import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import axios from "api/axios";
import PageShell, { SectionCard } from "components/patch/PageShell";
import SimpleTable from "components/patch/SimpleTable";

const money = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function PayrollSnapshots() {
  const [month, setMonth] = useState("");
  const [runId, setRunId] = useState("");
  const [pfNo, setPfNo] = useState("");
  const [snapshots, setSnapshots] = useState([]);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [verification, setVerification] = useState(null);
  const [payslip, setPayslip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const selectedId = selected?.id;

  const loadSnapshots = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get("/payroll-snapshots", {
        params: { month: month || undefined, per_page: 50 },
      });
      const list = res.data?.data?.data || res.data?.data || [];
      setSnapshots(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e.response?.data?.message || "Unable to load payroll snapshots.");
    } finally {
      setLoading(false);
    }
  };

  const loadItems = async (snapshot) => {
    if (!snapshot?.id) return;
    setSelected(snapshot);
    setVerification(null);
    setPayslip(null);
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/payroll-snapshots/${snapshot.id}/items`, {
        params: { pf_no: pfNo || undefined, per_page: 100 },
      });
      const list = res.data?.data?.data || res.data?.data || [];
      setItems(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e.response?.data?.message || "Unable to load snapshot items.");
    } finally {
      setLoading(false);
    }
  };

  const createSnapshot = async () => {
    if (!runId) {
      setError("Enter a finalized payroll run ID first.");
      return;
    }
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const res = await axios.post(`/payroll-snapshots/runs/${runId}`);
      setMessage(res.data?.message || "Snapshot created successfully.");
      await loadSnapshots();
    } catch (e) {
      setError(e.response?.data?.message || "Unable to create snapshot.");
    } finally {
      setLoading(false);
    }
  };

  const sealSnapshot = async (snapshot) => {
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const res = await axios.post(`/payroll-snapshots/${snapshot.id}/seal`);
      setMessage(res.data?.message || "Snapshot sealed successfully.");
      await loadSnapshots();
    } catch (e) {
      setError(e.response?.data?.message || "Unable to seal snapshot.");
    } finally {
      setLoading(false);
    }
  };

  const verifySnapshot = async (snapshot) => {
    setLoading(true);
    setError(null);
    setVerification(null);
    try {
      const res = await axios.get(`/payroll-snapshots/${snapshot.id}/verify`);
      setVerification(res.data?.data || null);
    } catch (e) {
      setError(e.response?.data?.message || "Unable to verify snapshot.");
    } finally {
      setLoading(false);
    }
  };

  const viewPayslip = async (row) => {
    if (!selectedId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/payroll-snapshots/${selectedId}/payslip`, {
        params: { pf_no: row.pf_no },
      });
      setPayslip(res.data?.data || null);
    } catch (e) {
      setError(e.response?.data?.message || "Unable to load payslip snapshot.");
    } finally {
      setLoading(false);
    }
  };

  const exportSnapshot = (snapshot) => {
    window.open(`${axios.defaults.baseURL}/payroll-snapshots/${snapshot.id}/export.csv`, "_blank");
  };

  useEffect(() => {
    loadSnapshots();
  }, []);

  const snapshotColumns = useMemo(
    () => [
      { label: "Code", key: "snapshot_code" },
      { label: "Month", key: "month" },
      {
        label: "Status",
        render: (row) => (
          <Chip
            size="small"
            label={row.status}
            color={row.status === "sealed" ? "success" : "warning"}
          />
        ),
      },
      { label: "Staff", key: "staff_count" },
      { label: "Gross", render: (row) => money(row.gross_total) },
      { label: "Deductions", render: (row) => money(row.deduction_total) },
      { label: "Net", render: (row) => money(row.net_total) },
      {
        label: "Actions",
        render: (row) => (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button size="small" variant="outlined" onClick={() => loadItems(row)}>
              View
            </Button>
            <Button size="small" variant="outlined" onClick={() => verifySnapshot(row)}>
              Verify
            </Button>
            <Button size="small" variant="outlined" onClick={() => exportSnapshot(row)}>
              CSV
            </Button>
            {row.status !== "sealed" ? (
              <Button size="small" variant="contained" onClick={() => sealSnapshot(row)}>
                Seal
              </Button>
            ) : null}
          </Stack>
        ),
      },
    ],

    []
  );

  const itemColumns = useMemo(
    () => [
      { label: "PF No", key: "pf_no" },
      { label: "Name", key: "staff_name" },
      { label: "Department", key: "department" },
      { label: "Gross", render: (row) => money(row.gross_pay) },
      { label: "Deductions", render: (row) => money(row.total_deductions) },
      { label: "Net", render: (row) => money(row.net_pay) },
      { label: "Employer Pension", render: (row) => money(row.employer_pension) },
      {
        label: "Action",
        render: (row) => (
          <Button size="small" onClick={() => viewPayslip(row)}>
            Payslip
          </Button>
        ),
      },
    ],

    [selectedId]
  );

  return (
    <PageShell
      title="Payroll Snapshots & Versioning"
      subtitle="Create immutable payroll snapshots for audit reconstruction, historical payslips, sealed run records, and exact payroll review after finalization."
      actions={
        <Button variant="contained" disabled={loading} onClick={loadSnapshots}>
          Refresh
        </Button>
      }
    >
      <Stack spacing={2.5}>
        {message ? (
          <Alert severity="success" onClose={() => setMessage(null)}>
            {message}
          </Alert>
        ) : null}
        {error ? (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        <SectionCard
          title="Create Snapshot"
          subtitle="Use this only for finalized or approved payroll runs. The snapshot stores the exact run and staff-level payroll result."
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Payroll Run ID"
                value={runId}
                onChange={(e) => setRunId(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md="auto">
              <Button variant="contained" disabled={loading} onClick={createSnapshot}>
                Create Snapshot
              </Button>
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard
          title="Snapshot Register"
          subtitle="Filter by month and open any historical payroll snapshot."
        >
          <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Month e.g. 2026-04"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md="auto">
              <Button variant="outlined" disabled={loading} onClick={loadSnapshots}>
                Apply Filter
              </Button>
            </Grid>
          </Grid>
          <SimpleTable columns={snapshotColumns} rows={snapshots} />
        </SectionCard>

        {verification ? (
          <SectionCard title="Snapshot Verification Result">
            <Typography variant="body2">Snapshot: {verification.snapshot_code}</Typography>
            <Typography variant="body2">Status: {verification.status}</Typography>
            <Typography variant="body2">Rows checked: {verification.staff_count}</Typography>
            <Typography variant="body2">Row issues: {verification.row_issue_count}</Typography>
            <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
              Checksum: {verification.stored_checksum}
            </Typography>
          </SectionCard>
        ) : null}

        {selected ? (
          <SectionCard
            title={`Snapshot Items - ${selected.snapshot_code}`}
            subtitle="These records are read from the immutable snapshot, not from live payroll tables."
          >
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Search PF No"
                  value={pfNo}
                  onChange={(e) => setPfNo(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md="auto">
                <Button variant="outlined" onClick={() => loadItems(selected)}>
                  Search
                </Button>
              </Grid>
            </Grid>
            <SimpleTable columns={itemColumns} rows={items} />
          </SectionCard>
        ) : null}
      </Stack>

      <Dialog open={Boolean(payslip)} onClose={() => setPayslip(null)} maxWidth="md" fullWidth>
        <DialogTitle>Payslip Snapshot</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {payslip?.staff_name} — {payslip?.pf_no} — {payslip?.month}
          </Typography>
          <Box
            component="pre"
            sx={{ whiteSpace: "pre-wrap", fontSize: 13, p: 2, bgcolor: "#f7f9fc", borderRadius: 2 }}
          >
            {JSON.stringify(payslip?.payslip || payslip?.raw || {}, null, 2)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayslip(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
